import { Alert, Link, ScrollArea } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { AngleLeftLightIcon } from '@mm/icons';
import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { useCampaignPurposesCRUDActions } from '@mm/state/actions/CampaignPurposesCRUDActions';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { useTagsCRUDActions } from '@mm/state/actions/TagsCRUDActions';
import { useTwilioCRUDActions } from '@mm/state/actions/TwilioCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import {
  CampaignSchedulerAttribute,
  CampaignScheduleType,
  CampaignType,
  PlatformType,
} from '@mm/state/model/CampaignsModel';
import { ChannelName } from '@mm/state/model/ChatbotModel';
import PageModel from '@mm/state/model/PageModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  CampaignTypeWithMappedPurposeAndDisplayStatus,
  selectCampaignById,
} from '@mm/state/selectors/campaignSelectors';
import {
  activeFacebookPagePlanInfoSelector,
  activeFacebookPageSelector,
  activeMessagesStatisticSelector,
} from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectAudienceByIdOrIds } from '@mm/state/selectors/messengerAdSelectors';
import { pageEntityByIdSelector } from '@mm/state/selectors/pageSelectors';
import {
  getTwilioAvailable,
  selectActiveChatbot,
} from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { Location } from 'history';
import { isNumber } from 'lodash';
import { uniqueId } from 'lodash/fp';
import moment from 'moment';
import { match } from 'path-to-regexp';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Prompt, useHistory } from 'react-router-dom';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { GreenMarker } from '../../../../../common/components/GreenMarker/GreenMarker';
import Spinner from '../../../../../common/components/Spinner';
import TextInput from '../../../../../common/components/TextInput';
import { useBotChannels } from '../../../../../common/hooks/use-bot-channels';
import { getDeviceInfo } from '../../../../../common/hooks/use-device-info';
import { useGreenCueMarkers } from '../../../../../common/hooks/use-green-cue-markers';
import { useFreeTrialUpgradeModal } from '../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import { useInactiveBotActivator } from '../../../../common/hooks/use-inactive-bot-activator';
import { useRouteParams } from '../../../beta/hooks/use-route-params';
import { NeedHelpLabel } from '../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { blasterCardTypes } from '../../components/BlastTypeSelector/blasterCardTypes';
import { findPurposeId } from '../../components/BlastTypeSelector/BlastTypeSelector';
import { InactiveChannelAlert } from '../DripCampaignDetailsPanel/InactiveChannelAlert';
import './ChatBlasterDetailsPanel.scss';
import { ChatBlasterDialogueEditor } from './ChatBlasterDialogueEditor/ChatBlasterDialogueEditor';
import {
  ChatBlasterPreviewByChannels,
  PreviewChannelOption,
} from './ChatBlasterPreviewByChannels/ChatBlasterPreviewByChannels';
import { ChatBlasterSteps } from './ChatBlasterSteps/ChatBlasterSteps';
import { SmsIphonePreview } from '../../components/SmsIphonePreview/SmsIphonePreview';

// based on new UI requirements

export const OTN_PURPOSE_ID = 20;
export const SUBSCRIPTION_MESSAGING_PURPOSE = 102;

export const getContactsCount = (
  purpose?: number,
  audience?: AudiencesModel,
  platforms?: { facebook: boolean; sms: boolean },
  tagName?: string,
): number => {
  if (
    !audience ||
    !platforms ||
    !Object.values(platforms).some(platform => platform)
  ) {
    return 0;
  }

  if (
    purpose === blasterCardTypes.promoMessage.purposeId ||
    (purpose === OTN_PURPOSE_ID && !tagName)
  ) {
    return platforms.facebook && platforms.sms
      ? audience.reachableContactsCount
      : platforms.facebook
      ? audience.reachableFacebookContactsCount || 0
      : audience.reachableSmsContactsCount;
  }
  if (purpose === OTN_PURPOSE_ID && tagName) {
    const otnCount =
      (audience.otnReachableContactsCounts || []).find(
        count => count.tag === tagName,
      )?.count || audience.reachableContactsCount;
    return platforms.facebook && platforms.sms
      ? otnCount
      : platforms.facebook
      ? otnCount - audience.reachableSmsContactsCount
      : audience.reachableSmsContactsCount;
  }

  return platforms.facebook && platforms.sms
    ? audience.contactsCount
    : platforms.facebook
    ? audience.contactsCount - audience.smsContactsCount
    : audience.smsContactsCount;
};

const getCreditsSendMessage = (
  platforms: CampaignType['platforms'],
  credits: CampaignType['possibleCredits'],
  size: number,
) => {
  const base = `This chat blast will be sent to ${size} ${pluralize(
    'contact',
    size,
  )}`;
  let price = 0;
  const confirm = 'Click Send to confirm.';

  if (platforms && credits) {
    const totalCredits = platforms.reduce(
      (total, platform) => total + credits[platform],
      0,
    );

    price = totalCredits;

    // temporary disable per request
    // platforms.forEach((platform, i) => {
    //   message += ` (${credits[platform]} - ${platform})`;
    //   message += i + 1 !== platforms.length ? ',' : '.';
    // });
  }

  return [base, price, confirm];
};

export interface CampaignErrors {
  name?: string;
  platforms?: string;
  audience?: string;
  dialogue?: string;
}

export const validateCampaign = (campaign: CampaignType) => {
  const { name, audienceId, platforms } = campaign;
  const errors = {} as CampaignErrors;

  const platformsValid = !!platforms?.length;

  if (!name || !name.trim()) {
    errors.name = 'Campaign Name Required';
  }

  if (!platformsValid) {
    errors.platforms = 'Platform Required';
  }

  if (!audienceId) {
    errors.audience = 'Audience Required';
  }

  return errors;
};

export const mapStateCampaignForUpdate = (
  campaign: CampaignTypeWithMappedPurposeAndDisplayStatus,
): CampaignType => {
  const entity = {} as CampaignType;
  entity.id = campaign.id;
  entity.name = campaign.name;

  if (campaign.audienceId) {
    entity.audienceId = campaign.audienceId;
  }

  entity.platforms = campaign.platforms;
  entity.scheduleType = campaign.scheduleType;
  entity.purpose = campaign.purpose;
  entity.tags = campaign.tags;
  entity.active = campaign.active;

  const { scheduledAt, id, destinationPageId } =
    campaign.schedulersAttributes?.[0] || {};
  if (scheduledAt || destinationPageId) {
    entity.schedulersAttributes = [
      { scheduledAt, id, destinationPageId },
    ] as CampaignSchedulerAttribute[];
  }

  // reset saved purposes and tags in case SMS only platform
  if (campaign.platforms?.join('') === 'sms') {
    entity.purpose = blasterCardTypes.promoMessage.purposeId;
    entity.tags = [];
  }

  return entity;
};

const ChatBlasterForm = ({
  campaign,
  dialogue,
}: {
  campaign: CampaignTypeWithMappedPurposeAndDisplayStatus;
  dialogue: PageModel;
}) => {
  const history = useHistory();
  const chatbot = useSelector(selectActiveChatbot);
  const chatbotId = chatbot?.id;
  const { isMobile } = getDeviceInfo();

  const [stateCampaign, setStateCampaign] = useState(campaign);
  const [activePreview, setActivePreview] =
    useState<PreviewChannelOption>('facebook');

  const [diffKey, setDiffKey] = useState(uniqueId('state-campaign-'));

  const canSendBlasts = useSelector(
    combinedFeatureFlagsSelector,
  ).campaignsBlasterSend;

  const showActivationCues = useSelector(
    combinedFeatureFlagsSelector,
  ).show_cues;

  const modalActions = useModalActions();

  const {
    isFreeTrialPromotionWithoutSubscription,
    isFreeTrialPromotionWithFutureSubscription,
  } = useFreeTrialUpgradeModal();
  const { isChatBlasterActivated } = useGreenCueMarkers();

  const containerClassName = classNames('chat-blaster-details');

  const redirectToIndex = (state?: Record<string, any>) => {
    if (chatbotId) {
      history.push(
        BotBuilderRoutes.campaigns.getLink({ chatbotId, kind: 'chat_blaster' }),
        state,
      );
    }
  };

  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const isPreviewAvailable = activeFacebookPage && dialogue;
  const previewLink = `http://m.me/${activeFacebookPage?.remoteId}?ref=${dialogue?.searchToken}`;

  const onPreviewBtnClick = () => {
    if (isPreviewAvailable) {
      window.open(previewLink);
    }
  };

  const purposes = useSelector(
    (state: StoreState) => state.campaignPurposes.purposes,
  );

  const purposeId = findPurposeId(
    purposes,
    stateCampaign.purpose,
    stateCampaign.purposeName,
  );

  const campaignCRUDActions = useCampaignCRUDActions();
  const updateCampaignFromStateChanges = () => {
    if (chatbotId) {
      return campaignCRUDActions.updateCampaign(
        mapStateCampaignForUpdate({
          ...stateCampaign,
          purpose: purposeId || 10,
        }),
        chatbotId,
      );
    }
  };

  const setCampaign = (
    update: CampaignTypeWithMappedPurposeAndDisplayStatus,
  ) => {
    const id = uniqueId('state-campaign-');
    setStateCampaign(update);
    setDiffKey(id);
  };

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCampaign({ ...stateCampaign, name: e.target.value });

  const onPlatformsChange = (platforms: PlatformType[]) => {
    setCampaign({ ...stateCampaign, platforms });
  };

  const onPurposeChange = (purpose: number, tagName?: string) => {
    // also reset tags property on purpose change
    setCampaign({
      ...stateCampaign,
      purpose,
      tags: tagName ? [{ name: tagName }] : [],
    });
  };
  const onTagNameChange = useCallback(
    (name?: string) => {
      setCampaign({ ...stateCampaign, tags: name ? [{ name }] : [] });
    },
    [stateCampaign.purpose, diffKey, stateCampaign],
  );

  const onSelectedDateChange = useCallback(
    (scheduledAt: moment.Moment) => {
      setCampaign({
        ...stateCampaign,
        schedulersAttributes: [
          {
            ...stateCampaign.schedulersAttributes?.[0],
            scheduledAt: scheduledAt.toString(),
          } as CampaignSchedulerAttribute,
        ],
      });
    },
    [diffKey, stateCampaign],
  );

  const onDialogueChange = (dialogueId?: number) => {
    setCampaign({
      ...stateCampaign,
      schedulersAttributes: [
        {
          ...stateCampaign.schedulersAttributes?.[0],
          destinationPageId: dialogueId,
        } as CampaignSchedulerAttribute,
      ],
    });
  };

  const onRecipientTimeZoneChange = (shouldUse: boolean) => {
    setCampaign({
      ...stateCampaign,
      scheduleType: shouldUse ? 'timezone' : 'global',
    });
  };

  const onSendImmediatelyChange = (shouldSend: boolean) => {
    const up = {
      ...stateCampaign,
      scheduleType: shouldSend ? 'none' : ('timezone' as CampaignScheduleType),
    };

    const scheduledAt = !shouldSend
      ? String(moment().add(1, 'day').hours(9).startOf('hour'))
      : null;

    up.schedulersAttributes = [
      {
        ...stateCampaign.schedulersAttributes?.[0],
        scheduledAt,
      } as CampaignSchedulerAttribute,
    ];

    setCampaign(up);
  };

  const onAudienceSelect = (audienceId: number) => {
    setCampaign({
      ...stateCampaign,
      audienceId,
      campaignAudiencesAttributes: [{ audienceId, kind: 'included' }], // api service reacts to audience update only to this field
    });
  };

  const { activateBotGuard } = useInactiveBotActivator();
  const { validateChannels, activateChannelGuard } = useBotChannels();

  const onSaveBtnClick = async () => {
    try {
      await updateCampaignFromStateChanges();
      redirectToIndex({ skipGuard: true });
    } catch (err) {
      // crud error handles this
    }
  };

  const activeFacebookPagePlanInfo = useSelector(
    activeFacebookPagePlanInfoSelector,
  ).info;
  const messagesStatistic = useSelector(activeMessagesStatisticSelector);

  const {
    openMonthlySendsWarningModal,
    openCampaignExceedsSendLimitModal,
    openGeneralConfirmModal,
    openFeatureActivationCongratsModal,
  } = useModalActions();

  const [audience] = useSelector((state: StoreState) =>
    selectAudienceByIdOrIds(state, stateCampaign.audienceId || null),
  );

  const platforms = {
    facebook: !!stateCampaign.platforms?.includes('facebook'),
    sms: !!stateCampaign.platforms?.includes('sms'),
  };

  const targetAudienceSize = getContactsCount(
    purposeId,
    audience,
    platforms,
    stateCampaign.tags?.[0]?.name,
  );

  const sendCampaign = (credits?: CampaignType['possibleCredits']) => {
    const { messagesRemaining } = messagesStatistic;

    const { isCustomTeam, isFreePlan, isLegacyPlan } =
      activeFacebookPagePlanInfo;

    const onCloseHandler = async (ok: boolean) => {
      if (ok) {
        if (chatbotId) {
          await campaignCRUDActions.updateCampaign(
            mapStateCampaignForUpdate({ ...stateCampaign, active: true }),
            chatbotId,
          );
        }
        setTimeout(
          () =>
            showActivationCues &&
            isFreeTrialPromotionWithoutSubscription &&
            openFeatureActivationCongratsModal({ feature: 'chat_blaster' }),
          1500,
        );
        redirectToIndex({ skipGuard: true });
      }
    };

    if (
      isFreePlan &&
      messagesRemaining === 0 &&
      !isFreeTrialPromotionWithFutureSubscription
    ) {
      return openMonthlySendsWarningModal();
    }
    const [base, price, confirm] = getCreditsSendMessage(
      stateCampaign?.platforms,
      credits,
      targetAudienceSize,
    );

    if (
      !activeFacebookPage?.overagesEnabled &&
      price > messagesRemaining &&
      isNumber(activeFacebookPage?.plan?.outgoingMessagesLimit) &&
      activeFacebookPage?.lockStrategy !== 'twilio_level' &&
      !isFreeTrialPromotionWithFutureSubscription
    ) {
      const isUnpaid =
        activeFacebookPagePlanInfo.isFreePlan ||
        isFreeTrialPromotionWithoutSubscription;
      const callToActionType = isUnpaid ? 'upgrade' : 'enable_overage';
      return openCampaignExceedsSendLimitModal(
        'Chat Blast',
        callToActionType,
        Number(price),
      );
    }

    const showCredits = !isCustomTeam && !isLegacyPlan;

    openGeneralConfirmModal({
      headerText: 'Chat Blast Confirmation',
      okButtonStyling: { blue: true },
      okButtonText: 'Send',
      cancelButtonFirst: true,
      messageText: (
        <div>
          <span>
            {base}
            {showCredits && (
              <>
                {price ? (
                  <strong>{` for total of ${price} Sends Credits.`}</strong>
                ) : (
                  '.'
                )}
                &nbsp;
                <br />
                {confirm}
              </>
            )}
          </span>
          {showCredits && (
            <>
              <br />
              <br />
              <span>
                <Link
                  isBlank={true}
                  href="https://mobilemonkey.com/help/article/kb9lgwacoj-mobile-monkey-send-limits"
                >
                  <strong>Learn more&nbsp;</strong>
                </Link>
                how this is calculated
              </span>
            </>
          )}
        </div>
      ),
      onCloseHandler,
    });
  };

  const onSendButtonClick = async () => {
    if (!canSendBlasts && platforms.sms) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames.CHAT_BLASTER_SEND_BUTTON,
        feature: 'campaignsBlasterSend',
      });
      return;
    }

    if (widgets.find(widget => !widget.validated.facebook) || !widgets.length) {
      openGeneralConfirmModal({
        headerText: 'Are you sure you want to send this blast?',
        messageText: !widgets.length ? (
          <div>
            Your dialogue is empty! We recommend that you add widgets before
            sending your Chat Blast
          </div>
        ) : (
          <div>
            Your dialogue contains errors! We recommend that you fix the errors
            before sending your Chat Blast
          </div>
        ),
        okButtonText: 'Send anyway',
        okButtonStyling: { gray: true },
        cancelButtonText: 'Cancel',
        cancelButtonColor: 'blue',
        cancelButtonFirst: true,
        onCloseHandler: (ok: boolean) => ok && prepareSendCampaign(),
      });
    } else prepareSendCampaign();
  };

  const prepareSendCampaign = () => {
    activateBotGuard()
      .then(() => activateChannelGuard(stateCampaign.platforms))
      .then(updateCampaignFromStateChanges) // saving here before send to retrieve possible credits
      .then(res => sendCampaign(res?.campaign?.possibleCredits))
      .catch(() => {}); // handled in the guard
  };

  const handleCancel = () => {
    redirectToIndex();
  };

  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogue.id, state.widget.entities),
  );

  const errors = useSelector((state: StoreState) => state.campaigns.errors);
  const errorMessage = errors ? errors.join('\n') : '';

  const validationErrors = validateCampaign(stateCampaign);
  if (!widgets.length) {
    validationErrors.dialogue = 'Dialogue Empty';
  }

  const invalidChannels = validateChannels(
    stateCampaign.platforms as any as ChannelName,
  );

  const isSaveDisabled = !stateCampaign.name || !stateCampaign.audienceId;
  const isCampaignValid = !Object.values(validationErrors).length;

  const renderSendButton = (className?: string) => {
    if (campaign.status !== 'canceled') {
      return (
        <div className="chat-blaster-details__send-button--wrapper">
          <PrimaryButton
            onClick={onSendButtonClick}
            disabled={!isCampaignValid}
            className={className}
          >
            Send
          </PrimaryButton>
          {!isChatBlasterActivated && isCampaignValid && (
            <GreenMarker className="chat-blaster-details__send-button--green-marker" />
          )}
        </div>
      );
    }
    return null;
  };

  const handleSaveBeforeGo = (
    location: Location<{ skipGuard?: boolean } | undefined>,
  ) => {
    const isChangingDialogue = match(BotBuilderRoutes.chatBlastEdit.path)(
      location.pathname,
    );

    if (location.state?.skipGuard || isChangingDialogue) {
      return true;
    }
    setTimeout(() => {
      openGeneralConfirmModal({
        headerText: 'Are you sure you want to leave this page?',
        messageText: (
          <div>
            Hey there! It looks like you&apos;re in the middle of writing
            something and you haven&apos;t saved all of your content.
            <br />
            <br />
            Save before you go!
          </div>
        ),
        okButtonText: 'Leave',
        cancelButtonText: 'Stay',
        cancelButtonFirst: true,
        onCloseHandler: (ok: boolean) => {
          if (ok) {
            history.push(location.pathname + location.search, {
              skipGuard: true,
            });
          }
        },
      });
    }, 500);
    return false;
  };

  return (
    <div className={containerClassName}>
      {!!invalidChannels.length &&
        invalidChannels.map(channel => (
          <InactiveChannelAlert channel={channel} type="chat blaster" />
        ))}

      <Prompt message={handleSaveBeforeGo} />

      <div className="drip-campaign-detail__title-area">
        <div
          className="drip-campaign-detail__back-button"
          onClick={() => {
            if (chatbotId) {
              history.push(
                BotBuilderRoutes.campaigns.getLink({
                  chatbotId,
                  kind: 'chat_blaster',
                }),
              );
            }
          }}
        >
          <AngleLeftLightIcon />
          {!isMobile && 'Back'}
        </div>
        <div className="drip-campaign-detail__main-header">
          <TextInput
            placeholder="Chat Blast Name"
            onChange={onNameChange}
            value={stateCampaign.name}
            className="drip-campaign-detail__name-input"
            maxLength={100}
          />

          {validationErrors.name && (
            <Alert
              className="drip-campaign-detail__error"
              type="error"
              showCloseIcon={false}
            >
              {validationErrors.name}
            </Alert>
          )}

          <div className="drip-campaign-detail__header-right-section">
            <NeedHelpLabel
              iconOnly={isMobile}
              style={isMobile ? { marginLeft: '-15px' } : {}}
              link="https://mobilemonkey.com/help/article/vc8wb3rpu6-send-a-mass-message-blaster"
            />

            <PrimaryButton
              color={ButtonColor.secondaryBlue}
              className="drip-campaign-detail__preview-button"
              onClick={onPreviewBtnClick}
              disabled={!isPreviewAvailable}
            >
              Preview
            </PrimaryButton>

            <PrimaryButton
              onClick={handleCancel}
              color={ButtonColor.secondaryBlue}
              className="drip-campaign-detail__button"
            >
              Cancel
            </PrimaryButton>

            <PrimaryButton
              color={ButtonColor.secondaryBlue}
              onClick={onSaveBtnClick}
              className="drip-campaign-detail__button"
              disabled={isSaveDisabled}
            >
              Save
            </PrimaryButton>

            {renderSendButton('drip-campaign-detail__button')}
          </div>
        </div>
      </div>

      <div className="chat-blaster-details__container">
        <ScrollArea className="chat-blaster-details__section_settings">
          <ChatBlasterSteps
            version={diffKey}
            errors={validationErrors}
            campaign={stateCampaign}
            purposeId={purposeId}
            dialogue={dialogue}
            renderSendButton={renderSendButton}
            targetAudienceSize={targetAudienceSize}
            onPlatformsChange={onPlatformsChange}
            onPurposeChange={onPurposeChange}
            onTagNameChange={onTagNameChange}
            onAudienceSelect={onAudienceSelect}
            onSelectedDateChange={onSelectedDateChange}
            onRecipientTimeZoneChange={onRecipientTimeZoneChange}
            onSendImmediatelyChange={onSendImmediatelyChange}
            setActivePreview={setActivePreview}
          />
        </ScrollArea>

        <ScrollArea className="chat-blaster-details__section_content">
          <div className="chat-blaster-details__section_content_container">
            <ChatBlasterDialogueEditor
              purposeId={purposeId}
              campaign={stateCampaign}
              version={diffKey}
              dialogue={dialogue}
              onDialogueChange={onDialogueChange}
              errors={validationErrors}
            />
          </div>
        </ScrollArea>

        <div className="chat-blaster-details__section-preview">
          <ChatBlasterPreviewByChannels
            dialogueId={dialogue.id}
            channels={stateCampaign.platforms}
            activePreview={activePreview}
            setActivePreview={setActivePreview}
            link={previewLink}
            campaignId={stateCampaign.id}
            schedulerId={stateCampaign.schedulersAttributes?.[0].id}
            activeTestNumbers={
              stateCampaign.schedulersAttributes?.[0].testNumbers
            }
            onSaveCampaign={updateCampaignFromStateChanges}
            platforms={stateCampaign.platforms}
          />
        </div>
      </div>
    </div>
  );
};

const useFetchCampaignAndDialogue = () => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  const widgetCRUDActions = useWidgetCRUDActions();
  const twilioCRUDActions = useTwilioCRUDActions();
  const pageCRUDActions = usePageCRUDActions();

  const { chatbotId, campaignId, dialogueId } = useRouteParams();

  const twilioFeatFlag = useSelector(combinedFeatureFlagsSelector).twilio;
  const twilioAvailableFlag = useSelector(getTwilioAvailable);
  const smsSupported = twilioFeatFlag || twilioAvailableFlag;

  const campaign = useSelector((state: StoreState) =>
    selectCampaignById(state, Number(campaignId)),
  );
  const dialogue = useSelector((state: StoreState) =>
    pageEntityByIdSelector(state, dialogueId),
  );

  const audiencesCRUDActions = useAudiencesCRUDActions();
  const campaignPurposesCRUDActions = useCampaignPurposesCRUDActions();
  const tagsCRUDActions = useTagsCRUDActions();

  useEffect(() => {
    const run = async () => {
      const actions = [
        campaignPurposesCRUDActions.retrieveAll(),
        audiencesCRUDActions.retrieveAll(Number(chatbotId), 1, 50, '', true),
        smsSupported &&
          activeFacebookPage &&
          twilioCRUDActions.getAll(activeFacebookPage.id, smsSupported),
        activeFacebookPage &&
          tagsCRUDActions.getAll(activeFacebookPage.id, false, [
            'otn',
            'regular',
          ]),
      ];

      await Promise.all(actions);
    };
    run();
  }, [chatbotId, activeFacebookPage]);

  const campaignsCRUDActions = useCampaignCRUDActions();
  useEffect(() => {
    (async () => {
      if (campaignId) {
        await campaignsCRUDActions.retrieve(Number(campaignId));
      }
    })();
  }, [campaignId]);

  const [isLoadingDialogue, setIsLoadingDialogue] = useState(true);
  useEffect(() => {
    (async () => {
      if (dialogueId) {
        await Promise.all([
          widgetCRUDActions.retrieveAll(Number(dialogueId), true),
          pageCRUDActions.retrieveById(Number(dialogueId)),
        ]);
        setIsLoadingDialogue(false);
      }
    })();
    return () => {
      widgetCRUDActions.unsetEntities();
    };
  }, [dialogueId]);

  const history = useHistory();
  // if dialogue id is not provided via route param, grab first available destination page id
  // this is done due to poor design where each blaster campaign requires dialogue id to be set on create but poor to retrieve in collection views
  const destinationPageId =
    campaign?.schedulersAttributes?.[0].destinationPageId;
  useEffect(() => {
    if (!dialogueId && destinationPageId && chatbotId && campaignId) {
      const path = BotBuilderRoutes.chatBlastEdit.getLink({
        chatbotId: Number(chatbotId),
        campaignId: Number(campaignId),
        dialogueId: destinationPageId,
      });
      history.replace(path);
    }
  }, [dialogueId, destinationPageId]);

  return { campaign, dialogue, isLoadingDialogue };
};

export const ChatBlasterDetailsPanel = () => {
  const { campaign, dialogue, isLoadingDialogue } =
    useFetchCampaignAndDialogue();

  if (!campaign || !dialogue || isLoadingDialogue) {
    return (
      <span className="chat-blaster-details__loader">
        <Spinner standalone />
      </span>
    );
  }

  return <ChatBlasterForm campaign={campaign} dialogue={dialogue} />;
};
